import moment, { Moment } from 'moment-timezone';
import { useEffect, useMemo, useState } from 'react';
import { Calendar } from './types';

interface UseDateRangePickerSelectorParams {
  onValueChange?: (value: { from: Moment | null; to: Moment | null }) => void;
}

export const useDateRangePickerSelector = (params: UseDateRangePickerSelectorParams) => {
  const { onValueChange } = params;
  const [selectedDateRange, setSelectedDateRange] = useState<{
    from: Moment | null;
    to: Moment | null;
  }>({
    from: null,
    to: null
  });

  const [filter, setFilter] = useState<{
    month: number;
    year: number;
  }>({
    month: moment().month() + 1,
    year: moment().year()
  });

  const calendarLeft = useMemo(() => {
    return new Calendar({
      month: filter.month,
      year: filter.year
    });
  }, [filter]);

  const calendarRight = useMemo(() => {
    return new Calendar({
      month: filter.month + 1,
      year: filter.year
    });
  }, [filter]);

  useEffect(() => {
    onValueChange &&
      onValueChange({
        from: selectedDateRange.from,
        to: selectedDateRange.to
      });
  }, [selectedDateRange]);

  const onPreviousMonth = () => {
    if (filter.month === 1) {
      setFilter({
        month: 12,
        year: filter.year - 1
      });
    } else {
      setFilter({
        month: filter.month - 1,
        year: filter.year
      });
    }
  };

  const onNextMonth = () => {
    if (filter.month === 12) {
      setFilter({
        month: 1,
        year: filter.year + 1
      });
    } else {
      setFilter({
        month: filter.month + 1,
        year: filter.year
      });
    }
  };

  const onNextYear = () => {
    setFilter({
      month: filter.month,
      year: filter.year + 1
    });
  };

  const onYear = (year: number) => {
    setFilter({
      month: filter.month,
      year: year
    });
  };

  const onPreviousYear = () => {
    setFilter({
      month: filter.month,
      year: filter.year - 1
    });
  };

  const onReset = () => {
    setSelectedDateRange({
      from: null,
      to: null
    });
  };

  const onSelectDate = (date: Moment | null) => {
    if (!date) return;
    if (!selectedDateRange.from) {
      setSelectedDateRange({
        from: date,
        to: null
      });
      return;
    }

    if ((selectedDateRange.from && selectedDateRange.to) || date.isBefore(selectedDateRange.from)) {
      setSelectedDateRange({
        from: date,
        to: null
      });
      return;
    }

    setSelectedDateRange({
      from: selectedDateRange.from,
      to: date
    });
  };

  return {
    month: filter.month,
    year: filter.year,
    calendarLeft: calendarLeft.getCalendar(),
    calendarRight: calendarRight.getCalendar(),
    selectedDateRange,
    onSelectDate,
    onPreviousMonth,
    onNextMonth,
    onPreviousYear,
    onNextYear,
    onReset,
    onYear
  };
};
